import { collection, addDoc, Timestamp, query, where, getDocs } from 'firebase/firestore';
import { db } from './firebase';

export type NotificationType =
  | 'nps_submission'
  | 'manager_checkin'
  | 'leave_request_submitted'
  | 'leave_status_update'
  | 'success_nomination';

export interface NotificationMetadata {
  npsRating?: number;
  checkinType?: string;
  submittedBy?: string;
  submittedByName?: string;
  programmeTags?: string[];
  excerpt?: string;
  staffUid?: string;
  staffName?: string;
  nominationId?: string;
  sessionsCompleted?: number;
  clientLocation?: string;
}

export type NotificationRoleAudience = 'ADMIN' | 'MANAGER' | 'USER';

export interface NotificationRecipients {
  coachIds?: string[];
  roles?: NotificationRoleAudience[];
  locations?: string[];
}

export interface NotificationInitiator {
  name?: string;
  coachId?: string;
  role?: NotificationRoleAudience;
  uid?: string;
}

export interface CreateNotificationParams {
  type: NotificationType;
  clientId: string;
  clientName: string;
  message?: string;
  metadata?: NotificationMetadata;
  recipients?: NotificationRecipients;
  leadCoachId?: string | number | null;
  managerLocations?: Array<string | null | undefined>;
  submittedBy?: NotificationInitiator;
  eventKey?: string;
}

const normaliseCoachId = (coachId?: string | number | null): string | undefined => {
  if (coachId === null || coachId === undefined) return undefined;
  return typeof coachId === 'string' ? coachId : coachId.toString();
};

// Firestore rejects any document containing an `undefined` value, so optional
// fields must be dropped (or written as null) before the write.
const stripUndefined = <T extends object>(value: T): Partial<T> =>
  Object.entries(value as Record<string, unknown>).reduce<Record<string, unknown>>((acc, [key, entry]) => {
    if (entry !== undefined) acc[key] = entry;
    return acc;
  }, {}) as Partial<T>;

const buildMessage = (
  type: NotificationType,
  clientName: string,
  metadata?: NotificationMetadata,
  fallback?: string
): string => {
  if (fallback) return fallback;

  if (type === 'nps_submission' && metadata?.npsRating !== undefined) {
    return `${clientName} submitted an NPS rating of ${metadata.npsRating}/5`;
  }

  if (type === 'manager_checkin' && metadata?.checkinType) {
    const submitter = metadata.submittedByName || 'Manager';
    return `${submitter} submitted a ${metadata.checkinType} check-in for ${clientName}`;
  }

  if (type === 'leave_request_submitted' && metadata?.submittedByName) {
    return `${metadata.submittedByName} submitted a new leave request`;
  }

  if (type === 'leave_status_update' && metadata?.submittedByName && metadata?.checkinType) {
    return `${metadata.submittedByName} ${metadata.checkinType} a leave request`;
  }

  if (type === 'success_nomination') {
    const nominator = metadata?.submittedByName || 'A team member';
    return `${nominator} nominated ${clientName} for Client Success`;
  }

  return `New notification for ${clientName}`;
};

/**
 * Writes a notification document. Never throws: a failed notification must not
 * roll back the business action that triggered it. Returns `true` when the
 * document was written so callers can warn the user when delivery failed.
 */
export const createNotification = async (params: CreateNotificationParams): Promise<boolean> => {
  const {
    type,
    clientId,
    clientName,
    message,
    metadata,
    recipients,
    leadCoachId,
    managerLocations,
    submittedBy,
    eventKey,
  } = params;

  const leadCoachIdStr = normaliseCoachId(leadCoachId);

  const recipientsCoachIds = new Set<string>();
  if (leadCoachIdStr) {
    recipientsCoachIds.add(leadCoachIdStr);
  }
  if (recipients?.coachIds) {
    recipients.coachIds
      .map(normaliseCoachId)
      .filter((value): value is string => Boolean(value))
      .forEach((value) => recipientsCoachIds.add(value));
  }

  const recipientsRoles = new Set<NotificationRoleAudience>();
  if (recipients?.roles) {
    recipients.roles.forEach((role) => recipientsRoles.add(role));
  }

  const recipientsLocations = new Set<string>();
  if (managerLocations) {
    managerLocations
      .filter((location): location is string => Boolean(location && location.trim()))
      .forEach((location) => recipientsLocations.add(location));
  }
  if (recipients?.locations) {
    recipients.locations
      ?.filter((location): location is string => Boolean(location && location.trim()))
      .forEach((location) => recipientsLocations.add(location));
  }

  const primaryManagerLocation = Array.from(recipientsLocations)[0];

  const createdAt = Timestamp.now();

  const cleanedInitiator = submittedBy
    ? Object.entries(submittedBy).reduce<Record<string, unknown>>((acc, [key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          acc[key] = value;
        }
        return acc;
      }, {})
    : null;

  const notificationData = {
    type,
    clientId,
    clientName,
    client: {
      id: clientId,
      name: clientName,
    },
    message: buildMessage(type, clientName, metadata, message),
    metadata: stripUndefined(metadata ?? {}),
    recipients: {
      coachIds: Array.from(recipientsCoachIds),
      roles: Array.from(recipientsRoles),
      locations: Array.from(recipientsLocations),
    },
    leadCoachId: leadCoachIdStr ?? null,
    managerLocation: primaryManagerLocation || null,
    timeline: {
      createdAt,
      eventKey: eventKey || null,
    },
    timestamp: createdAt,
    initiator: cleanedInitiator,
    status: {
      readBy: [] as string[],
      dismissedBy: [] as string[],
    },
    read: false,
    dismissed: false,
  };

  try {
    await addDoc(collection(db, 'notifications'), notificationData);
    return true;
  } catch (error) {
    console.error('Error creating notification:', error);
    return false;
  }
};

// Helper function to check if a notification already exists for a specific event
export const notificationExists = async (
  type: NotificationType,
  clientId: string,
  timestamp: Timestamp
): Promise<boolean> => {
  try {
    // Check for notifications of the same type for the same client within 5 minutes
    const fiveMinutesAgo = new Timestamp(
      timestamp.seconds - 300,
      timestamp.nanoseconds
    );
    
    const notificationsQuery = query(
      collection(db, 'notifications'),
      where('type', '==', type),
      where('clientId', '==', clientId),
      where('timestamp', '>=', fiveMinutesAgo),
      where('timestamp', '<=', timestamp)
    );
    
    const snapshot = await getDocs(notificationsQuery);
    return !snapshot.empty;
  } catch (error) {
    console.error('Error checking notification existence:', error);
    return false;
  }
};
